import { AssetsSyncRequestConfig, useStorytellClient } from "@storytell/client";
import { subscribeToGlobalEvent } from "@storytell/events";
import type { Asset } from "@storytell/types";
import { createResource, onCleanup, onMount } from "solid-js";
import { isServer } from "solid-js/web";
import { createResourceStorage } from "~/lib/createResourceStorage";
import { createEntitySyncManager } from "../sync/createEntitySyncManager";

export type AssetsService = ReturnType<typeof createAssetsService>;

export const createAssetsService = (deps: {
  authReady: () => boolean;
  authToken: () => string | undefined;
  userId: () => string | undefined;
  assetsSynced: () => void;
}) => {
  const client = useStorytellClient();

  const assets = createEntitySyncManager<Asset & { userId: string }>(undefined, (asset) => [
    asset.filename,
    asset.name,
  ]);

  const storage = createResourceStorage(
    () => assets.all(),
    (value) => {
      assets.hydrateFromSync(value || []);
      deps.assetsSynced();
    },
  );

  const [assetsData, { refetch }] = createResource(
    () => !isServer && deps.authReady() && deps.userId(),
    async (): Promise<(Asset & { userId: string })[]> => {
      const userId = deps.userId();
      if (!userId) return [];
      const request = AssetsSyncRequestConfig.withBearerToken(deps.authToken());
      const response = await client.fetch(request, undefined, undefined);
      if (response.isErr()) {
        console.error("Failed to fetch assets sync", response.error);
        return [];
      }

      const data = Object.values(response.value.assets).map((asset) => ({ ...asset, userId }));
      if (isServer) {
        storage[1](data);
      }
      return data;
    },
    {
      initialValue: [] as (Asset & { userId: string })[],
      deferStream: true,
      storage: () => storage,
    },
  );

  onMount(() => {
    const unsubscribeUserContext = subscribeToGlobalEvent("user-context-changed", () => {
      refetch();
    });
    const unsubscribeRefresh = subscribeToGlobalEvent("refresh-sync", () => {
      refetch();
    });
    onCleanup(() => {
      unsubscribeUserContext();
      unsubscribeRefresh();
    });
  });

  // This is used to trigger a suspense boundary so SSR works properly
  // Don't use it for data (that's why it's returned as undefined)
  const suspenseTrigger = () => (assetsData() ? undefined : undefined);

  return {
    suspenseTrigger,
    refetch,
    ...assets,
  };
};
